import { Reducer, AnyAction } from 'redux';
import { GenericSavedReport, SavedReportsCommonState } from './types-store';
import {
  ACTION,
  RequestReportEvaluation, ReceiveReportEvaluation, FailReportEvaluation,
  SelectCurrentSavedReports, UpdateCurrentSavedReports, UpdateCurrentValidation,
  RequestAddSavedReports, ReceiveAddSavedReports, FailAddSavedReports,
  UpdateSavedReport, DeleteSavedReport, DeleteAllSavedReports,
  SwitchDisplayDisabled, UpdateSavedReportValidation
} from './action-types';
import { clearValidationForKeys } from 'app/area/shared/report/form/validation';
import { CompanyReportItemValidation } from 'app/area/company-reports-administration/form/types-store';

const reducers = {

  [ACTION.REQUEST_REPORT_EVALUATION]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: RequestReportEvaluation
  ): SavedReportsCommonState<SavedReport> => {

    if (state.typeId !== action.typeId ||
      state.currentSavedReport?.code?.value !== action.favouriteId) {
      return state;
    }

    return {
      ...state,
      fetchingCurrentEvaluation: true
    };
  },

  [ACTION.RECEIVE_REPORT_EVALUATION]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: ReceiveReportEvaluation<SavedReport>
  ): SavedReportsCommonState<SavedReport> => {

    if (!state.fetchingCurrentEvaluation ||
      state.typeId !== action.typeId ||
      state.currentSavedReport?.code?.value !== action.favouriteId) {
      return state;
    }

    return {
      ...state,
      fetchingCurrentEvaluation: false,
      currentSavedReport: {
        ...state.currentSavedReport,
        code: action.reportCode,
        selectedReport: action.selectedReport,
        editable: action.editable
      },
      currentValidation: action.validation
    };
  },

  [ACTION.FAIL_REPORT_EVALUATION]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: FailReportEvaluation
  ): SavedReportsCommonState<SavedReport> => {

    if (!state.fetchingCurrentEvaluation ||
      state.typeId !== action.typeId ||
      state.currentSavedReport?.code?.value !== action.favouriteId) {
      return state;
    }

    return {
      ...state,
      fetchingCurrentEvaluation: false
    };
  },

  [ACTION.SELECT_CURRENT_SAVED_REPORT]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: SelectCurrentSavedReports<SavedReport>
  ): SavedReportsCommonState<SavedReport> => {

    const currentSavedReportId = action.savedReport?.code?.value;

    return {
      ...state,
      currentSavedReportId,
      currentSavedReport: action.savedReport,
      currentSavedReportOnlyIdMode: action.onlyIdMode,
      currentValidation: state.savedReportsValidation?.[currentSavedReportId]
    };
  },

  [ACTION.UPDATE_CURRENT_SAVED_REPORT]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: UpdateCurrentSavedReports<SavedReport>
  ): SavedReportsCommonState<SavedReport> => {

    const currentSavedReport = {
      ...(state.currentSavedReport || {}),
      ...action.savedReport
    } as SavedReport;

    const currentValidation = clearValidationForKeys(state.currentValidation,
      Object.keys(action.savedReport) as (keyof CompanyReportItemValidation)[]);

    return {
      ...state,
      currentSavedReport,
      currentValidation
    };
  },

  [ACTION.UPDATE_CURRENT_VALIDATION]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: UpdateCurrentValidation<SavedReport>
  ): SavedReportsCommonState<SavedReport> => {

    return {
      ...state,
      currentValidation: action.validation
    };
  },

  [ACTION.REQUEST_ADD_SAVED_REPORTS]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: RequestAddSavedReports
  ): SavedReportsCommonState<SavedReport> => {

    return {
      ...state,
      fetchingAddSavedReports: true
    };
  },

  [ACTION.RECEIVE_ADD_SAVED_REPORTS]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: ReceiveAddSavedReports<SavedReport>
  ): SavedReportsCommonState<SavedReport> => {

    const savedReports = !state.savedReports || state.savedReports.length === 0
      ? action.savedReports.map((report, index) => ({
        ...report,
        sequence: action.sequence + index
      }))
      : [
        ...state.savedReports
          .filter(report => report.sequence < action.sequence),
        ...action.savedReports.map((report, index) => ({
          ...report,
          sequence: action.sequence + index
        })),
        ...state.savedReports
          .filter(report => report.sequence >= action.sequence)
          .map((report) => ({
            ...report,
            sequence: report.sequence + action.savedReports.length
          }))
      ];

    return {
      ...state,
      savedReports: action.normalizeSequence(savedReports),
      fetchingAddSavedReports: false
    };
  },

  [ACTION.FAIL_ADD_SAVED_REPORTS]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: FailAddSavedReports
  ): SavedReportsCommonState<SavedReport> => {

    return {
      ...state,
      fetchingAddSavedReports: false
    };
  },

  [ACTION.UPDATE_SAVED_REPORT]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: UpdateSavedReport<SavedReport>
  ): SavedReportsCommonState<SavedReport> => {

    const currentSavedReportId = action.currentSavedReportId;
    const stateSavedReports = state.savedReports ?? [];
    const existingSavedReport = stateSavedReports.find(
      report => report.code?.value === currentSavedReportId
    );
    const currentSavedReport = existingSavedReport?.code && existingSavedReport.code.value === action.savedReport.code?.value
      ? {
        ...action.savedReport,
        code: existingSavedReport.code
      }
      : action.savedReport;

    const { [currentSavedReportId]: _, ...filteredValidation } = state.savedReportsValidation ?? {};
    const validation = Object.keys(action.validation || {}).length > 0
      ? {
        ...filteredValidation,
        [currentSavedReport?.code?.value]: action.validation
      }
      : filteredValidation;

    const sequence = currentSavedReport.sequence ?? 0;

    if (existingSavedReport?.sequence === sequence) {
      return {
        ...state,
        savedReports: [
          ...stateSavedReports.filter(report => report.sequence < sequence),
          currentSavedReport,
          ...stateSavedReports.filter(report => report.sequence >= sequence && report !== existingSavedReport)
        ],
        savedReportsValidation: validation
      };
    }

    const filteredSavedReports = existingSavedReport
      ? action.normalizeSequence(stateSavedReports.filter(report => report !== existingSavedReport))
      : stateSavedReports;

    const savedReports = [
      ...filteredSavedReports
        .filter(report => report.sequence < sequence),
      currentSavedReport,
      ...filteredSavedReports
        .filter(report => report.sequence >= sequence)
        .map(report => ({
          ...report,
          sequence: report.sequence + 1
        }))
    ];

    return {
      ...state,
      savedReports: action.normalizeSequence(savedReports),
      savedReportsValidation: validation
    };
  },

  [ACTION.DELETE_SAVED_REPORT]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: DeleteSavedReport<SavedReport>
  ): SavedReportsCommonState<SavedReport> => {

    const savedReport = state.savedReports?.find(report => report.code?.value === action.savedReportId);

    if (!savedReport) {
      return state;
    }

    const savedReports = state.savedReports
      .filter(report => report !== savedReport);
    const currentSavedReportId = savedReport.code?.value;
    const { [currentSavedReportId]: _, ...validation } = state.savedReportsValidation;

    return {
      ...state,
      savedReports: action.normalizeSequence(savedReports),
      savedReportsValidation: validation
    };
  },

  [ACTION.DELETE_ALL_SAVED_REPORTS]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: DeleteAllSavedReports
  ): SavedReportsCommonState<SavedReport> => {

    return {
      ...state,
      savedReports: [],
      savedReportsValidation: {}
    };
  },

  [ACTION.SWITCH_DISPLAY_DISABLED]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: SwitchDisplayDisabled
  ): SavedReportsCommonState<SavedReport> => {

    return {
      ...state,
      displayDisabled: action.displayDisabled
    };
  },

  [ACTION.UPDATE_VALIDATION]: <SavedReport extends GenericSavedReport>(
    state: SavedReportsCommonState<SavedReport>,
    action: UpdateSavedReportValidation<SavedReport>
  ): SavedReportsCommonState<SavedReport> => {

    return {
      ...state,
      savedReportsValidation: action.validation
    };
  }
};

export const withSavedReportsReducer =
  <SavedReport extends GenericSavedReport, S extends SavedReportsCommonState<SavedReport>>(
    component: string, reducer: Reducer<S>
  ) =>
    (state: S, action: AnyAction) => {
      const r = action.component === component && reducers[action.type];
      return ((r && r(state, action as any)) || reducer(state, action)) as S;
    };
